import {
  AgreementCampaign,
  AgreementResponse,
  BasicCompany,
  BasicSurveyCampaign,
  BasicSurveyEmployee,
  BasicSurveyTemplate,
  CategoryGroupedStatistics,
  Company,
  CompanySizes,
  CompanyUser,
  EmailTemplate,
  EmailVariables,
  FeatureFlag,
  FeatureFlagStatus,
  Gender,
  GroupResponse360Statistics,
  GroupResponseStatistics,
  IndividualResponse360Statistics,
  IndividualResponseStatistics,
  IsResetPasswordCodeValidFailureCode,
  QuestionStatistics,
  Response360StatisticsByTeam,
  Roles,
  SurveyCampaign,
  SurveyCampaignStatusLabels,
  SurveyEmployee,
  SurveyQuestion,
  SurveyQuestionResponse,
  SurveyResponse,
  SurveyTemplate,
  SurveyTemplateType,
  TeamStatistics,
} from "./types";

export type SignUpRequest = {
  companyUser?: {
    name: string;
    companyId: string;
    gender: Gender;
    email: string;
    mobile: string;
    password: string;
    role: Roles;
  };
};

export type SignUpResponse = {
  success?: boolean;
  message?: string;
  token?: string;
  expiresIn?: string;
  companyUser?: CompanyUser;
};

export type LoginRequest = {
  email: string;
  password: string;
  companySubdomain: string;
};

export enum LoginResponseErrorCode {
  UNAGREED_AGREEMENT_FOUND = "UNAGREED_AGREEMENT_FOUND",
}
export type LoginResponse = {
  success: boolean;
  errorCode: LoginResponseErrorCode;
  companyUser?: CompanyUser;
  accessToken: string;
  refreshToken: string;
  agreementResponse?: {
    tncFileLink?: string;
    agreementResponseId?: string;
  };
  message?: string;
};

export type GetProfileResponse = {
  success: boolean;
  companyUser?: CompanyUser;
  company?: Company;
  featureFlags?: FeatureFlag[];
  message?: string;
};

export type GetAllCompanyUserRequest = {};

export type GetAllCompanyUserResponse = {
  success: boolean;
  message?: string;
  companyUsers?: CompanyUser[];
};

export type GetCompanyUsersByCompanyIdRequest = {
  companyId?: string;
};

export type GetCompanyUsersByCompanyIdResponse = {
  success: boolean;
  message?: string;
  companyUsers?: CompanyUser[];
};

export type ChangePasswordRequest = {
  companyUserId?: string;
  oldPassword?: string;
  newPassword?: string;
};

export type ChangePasswordResponse = {
  success: boolean;
  message?: string;
};

export type SendResetPasswordEmailRequest = {
  email?: string;
  subDomain?: string;
};

export type SendResetPasswordEmailResponse = {
  success: boolean;
  message?: string;
};

export type IsResetPasswordCodeValidRequest = {
  token?: string;
};

export type IsResetPasswordCodeValidResponse = {
  success: boolean;
  failureCode?: IsResetPasswordCodeValidFailureCode;
  isValid?: boolean;
  message?: string;
};

export type ResetPasswordRequest = {
  newPassword?: string;
  token?: string;
};

export type ResetPasswordResponse = {
  success: boolean;
  message?: string;
};

export type UpdateCompanyUserRequest = {
  companyUser: {
    name?: string,
    email?: string,
    gender?: Gender,
    mobile?: string
  }
};


export type AdminUpdateCompanyUserRequest = {
  companyUser?: CompanyUser;
};

export type RefreshAccessTokenResponse = {
  success: boolean;
  message?: string;
  accessToken?: string;
  refreshToken?: string;
};

export type CreateCompanyRequest = {
  emailDomains?: string[];
  isAdmin?: boolean;
  subDomain?: string;
  helpEmail?: string;
  name?: string;
  logoFile?: File;
  industry?: string;
  size?: CompanySizes | "";
  location?: string;
  remark?: string;
};


export type UpdateCompanyRequest = {
  emailDomains?: string[];
  isAdmin?: boolean;
  subDomain?: string;
  helpEmail?: string;
  name?: string;
  logoFile?: File;
  industry?: string;
  size?: CompanySizes | "";
  location?: string;
  remark?: string;
};

export type UpdateCompanyResponse = {
  success: boolean;
  message?: string;
};

export type GetCompaniesResponse = {
  success: boolean;
  companies: Company[];
  message?: string;
};

export type GetSurveyTemplatesRequest = {
  name?: string;
  isGetDeleted?: boolean;
};

export type GetSurveyTemplatesResponse = {
  page: number;
  success: boolean;
  surveyTemplates: SurveyTemplate[];
  message?: string;
};

export type GetSurveyTemplatesBySurveyTemplateIdRequest = {
  surveyTemplateId?: string;
};

export type GetSurveyTemplatesBySurveyTemplateIdResponse = {
  success: boolean;
  surveyTemplate: SurveyTemplate;
  categories: { name: string; count: number; checked: boolean }[];
  message?: string;
};

export type GetSurveyCampaignsRequest = {
  limit?: number;
  skip?: number;
  status?: SurveyCampaignStatusLabels | string;
  isGetDeleted?: boolean;
  search?: string;
  type?: SurveyTemplateType | string;
  surveyTemplateId?: string;
  createdBefore?: number;
  email?: string;
};

export type GetSurveyCampaignsResponse = {
  success: boolean;
  surveyCampaigns: SurveyCampaign[];
  surveyTemplates: SurveyTemplate[];
  total?: number;
  message?: string;
};

export type GetSurveyTemplateDetailRequest = {
  surveyTemplateId: string;
};

export type GetSurveyTemplateDetailResponse = {
  success: boolean;
  surveyTemplate: SurveyTemplate | null;
  surveyTemplateQuestions: SurveyQuestion[] | null;
  message?: string;
};

export type GetSurveyCampaignBySurveyCampaignIdRequest = {
  surveyCampaignId?: string;
};

export type GetSurveyCampaignBySurveyCampaignIdResponse = {
  success: boolean;
  surveyCampaign: SurveyCampaign;
  categories: { name: string; count: number; checked: boolean }[];
  message?: string;
};

export type GetSurveyResponseBySurveyCampaignIdRequest = {
  surveyCampaignId?: string;
};

export type GetSurveyResponseBySurveyCampaignIdResponse = {
  success: boolean;
  surveyResponses: SurveyResponse[];
  message?: string;
};

export type GetSurveyResponseDetailRequest = {
  surveyResponseId?: string;
};

export type GetSurveyResponseDetailResponse = {
  success: boolean;
  message?: string;
  surveyResponse: SurveyResponse;
  participant: BasicSurveyEmployee;
  surveyCampaign: {
    questions: SurveyQuestion[];
    prompt: string;
    status: SurveyCampaignStatusLabels;
  };
  containsHiddenPaidOnlyQuestions: boolean;
};

export type GetSurveyTemplatePreviewRequest = {
  surveyTemplateId: string;
};

export type GetSurveyTemplatePreviewResponse = {
  success: boolean;
  surveyTemplate: SurveyTemplate | null;
  surveyQuestionResponses: SurveyQuestionResponse[] | null;
  message?: string;
};

export type GetSurveyCampaignGroupReportRequest = {
  surveyCampaignId?: string;
};

export type GetSurveyCampaignGroupReportResponse = {
  success: boolean;
  surveyCampaign?: SurveyCampaign;
  surveyTemplate?: SurveyTemplate;
  responseStatistics?: GroupResponseStatistics[];
  message?: string;
  numberOfCompleted?: number;
  numberOfCancelled?: number;
  numberOfResponse?: number;
};

export type GetSurveyCampaignBasicInfoRequest = {
  surveyCampaignId?: string;
};

export type GetSurveyCampaignBasicInfoResponse = {
  success: boolean;
  surveyCampaign?: SurveyCampaign;
  surveyTemplate?: SurveyTemplate;
  numberOfCompleted?: number;
  numberOfCancelled?: number;
  numberOfResponse?: number;
  participantTeams?: {
    team: string;
    numberOfParticipants: number;
  }[];
  surveyResponsesByParticipant?: {
    participant: SurveyResponse;
    surveyResponses: SurveyResponse[];
  }[];

  message?: string;
};

export type GetSurveyCampaignGroupReportV2Request = {
  surveyCampaignId?: string;
};

export type GetSurveyCampaignGroupReportV2Response = {
  success: boolean;
  surveyCampaign?: BasicSurveyCampaign;
  surveyTemplate?: BasicSurveyTemplate;
  categoryGroupedStatistics?: CategoryGroupedStatistics;
  teamStatistics?: TeamStatistics;
  questionStatistics?: QuestionStatistics[];
  highlightedQuestionStatistics?: QuestionStatistics[];
  numberOfCompleted?: number;
  numberOfCancelled?: number;
  numberOfResponse?: number;

  message?: string;
};
export type GetSurveyCampaignIndividualReportRequest = {
  surveyResponseId?: string;
};

export type GetSurveyCampaignIndividualReportResponse = {
  success: boolean;
  surveyCampaign?: SurveyCampaign;
  surveyTemplate?: SurveyTemplate;
  individualResponseStatistics: IndividualResponseStatistics[];
  message?: string;
};

export type GetSurveyCampaign360GroupReportRequest = {
  surveyCampaignId?: string;
};

export type GetSurveyCampaign360GroupReportResponse = {
  success: boolean;
  surveyCampaign?: SurveyCampaign;
  surveyTemplate?: SurveyTemplate;
  responseStatistics?: GroupResponse360Statistics[];
  message?: string;
  numberOfCompleted?: number;
  numberOfCancelled?: number;
  numberOfResponse?: number;
};

export type GetSurveyCampaign360TeamReportRequest = {
  surveyCampaignId?: string;
};

export type GetSurveyCampaign360TeamReportResponse = {
  success: boolean;
  surveyCampaign?: BasicSurveyCampaign;
  surveyTemplate?: BasicSurveyTemplate;
  company?: BasicCompany;
  responseStatisticsByTeam?: Response360StatisticsByTeam;
  message?: string;
  numberOfCompleted?: number;
  numberOfCancelled?: number;
  numberOfResponse?: number;
};

export type GetSurveyCampaign360IndividualReportRequest = {
  surveyResponseId?: string;
};
export type GetSurveyCampaign360IndividualReportResponse = {
  success: boolean;
  surveyResponse: SurveyResponse;
  surveyTemplate: SurveyTemplate;
  /**
   * 17/7/2022 daniel.kwok
   * responseStatistics is only applicable to participants
   * Filter out the rest
   * Participant is defined where surveyEmployee._id === surveyEmployee.participantId
   */
  responseStatistics?: IndividualResponse360Statistics[];
  message?: string;
};

export type GetSurveyResponseBySurveyResponseIdRequest = {
  surveyResponseId?: string;
};

export type GetSurveyResponseBySurveyResponseIdResponse = {
  success: boolean;
  surveyResponse?: SurveyResponse;
  participant?: SurveyEmployee;
  message?: string;
};

export type SaveSurveyResponseAsDraftRequest = {
  surveyResponse?: SurveyResponse;
};

export type SaveSurveyResponseAsDraftResponse = {
  success: boolean;
  message?: string;
};

export type ResendSurveyResponseEmailRequest = {
  surveyResponseIds?: string[];
};

export type ResendSurveyResponseEmailResponse = {
  success: boolean;
  message?: string;
};

export type CreateSurveyCampaignRequest = {
  surveyCampaign?: {
    surveyTemplateId: string;
    name: string;
    selectedCategories?: string[];
  };
};

export type CreateSurveyCampaignResponse = {
  success: boolean;
  surveyCampaignId: string;
  message?: string;
};

export type EnableSurveyTemplateRequest = {
  surveyTemplateId?: string;
};

export type EnableSurveyTemplateResponse = {
  success: boolean;
  message?: string;
};

export type DisableSurveyTemplateRequest = {
  surveyTemplateId?: string;
};

export type DisableSurveyTemplateResponse = {
  success: boolean;
  message?: string;
};

export type CreateSurveyTemplateRequest = {
  description: string;
  copyFromSurveyTemplateId: string;
  questions: {
    _id: string;
    category?: string[] | null;
    isPaidOnly?: boolean;
  }[];
};

export type SurveyTemplateResult = {
  active?: SurveyTemplate;
  past: SurveyTemplate[];
};

export type GetSurveyTemplatesAdminResponse = {
  success: boolean;
  bySurveyTemplateType: {
    [key: string]: SurveyTemplateResult;
  };
  bySurveyTemplateId: {
    [key: string]: SurveyTemplate;
  };
  message?: string;
};

export type DeleteSurveyCampaignRequest = {
  surveyCampaignId?: string;
};

export type UndeleteSurveyCampaignRequest = {
  surveyCampaignId?: string;
};

export type UndeleteSurveyCampaignResponse = {
  success: boolean;
  message?: string;
};

export type UpdateSurveyCampaignBasicRequest = {
  name: string;
  selectedCategories: string[];
};

export type UpdateSurveyCampaignEmployeesRequest = {
  surveyEmployees: {
    _id: string;
    fname: string;
    lname: string;
    email: string;
    gender: Gender;
    position: string;
    team?: string;
    participantId?: string;
  }[];
};

export type ValidateUpdateSurveyCampaignEmployeesRequest =
  UpdateSurveyCampaignEmployeesRequest;

export type SaveAsDraftUpdateSurveyCampaignEmployeesRequest =
  UpdateSurveyCampaignEmployeesRequest;

export type ValidateUpdateSurveyCampaignEmployeesErrorResponse = {
  responseCode: number;
  errorCode: string;
  message: string;
  details: {
    [key: string]: {
      [key: string]: string;
    };
  };
};

export type UpdateSurveyCampaignResponse = {
  success: boolean;
  message?: string;
};

export type UpsertSurveyResponseFromSurveyCampaignIdRequest = {
  surveyCampaignId?: string;
  surveyEmployees?: SurveyEmployee[];
};

export type UpsertSurveyResponseFromSurveyCampaignIdResponse = {
  success: boolean;
  message?: string;
};

export type GetEmailTemplateByCompanyIdRequest = {
  companyId?: string;
};

export type GetEmailTemplateByCompanyIdResponse = {
  success: boolean;
  message?: string;
  emailTemplates?: EmailTemplate[];
};

export type GetEmailTemplateByEmailTemplateIdRequest = {
  emailTemplateId?: string;
};

export type GetEmailTemplateByEmailTemplateIdResponse = {
  success: boolean;
  message?: string;
  emailTemplate?: EmailTemplate;
};

export type GetEmailTemplateVariablesRequest = {
  _any?: unknown;
};

export type GetEmailTemplateVariablesResponse = {
  success: boolean;
  message?: string;
  emailTemplateVariables?: EmailVariables;
};

export type UpdateEmailTemplateRequest = {
  emailTemplate?: EmailTemplate;
};

export type UpdateEmailTemplateResponse = {
  success: boolean;
  message?: string;
};

export type GetConstantsRequest = {
  keys?: string[];
  parsed?: boolean;
  surveyResponseId?: string;
};

export type GetConstantsResponse = {
  success: boolean;
  message?: string;
  constants?: Record<string, string>;
};

export type GetAgreementCampaignsResponse = {
  success: boolean;
  message?: string;
  agreementCampaigns: AgreementCampaign[];
};

export type UploadTncFileRequest = {
  base64: string;
  filename: string;
};

export type UploadTncFileResponse = {
  success: boolean;
  message?: string;
  presignedURL?: string;
};

export type GetUploadTncFilePresignedUrlRequest = {
  filename?: string;
};

export type GetUploadTncFilePresignedUrlResponse = {
  success: boolean;
  message?: string;
  presignedURL?: string;
};

export type CreateAgreementCampaignRequest = {
  name: string;
  tncFileLink: string;
};

export type CreateAgreementCampaignResponse = {
  success: boolean;
  message?: string;
  agreementCampaign?: AgreementCampaign;
};

export type GetAgreementCampaignByIDRequest = {
  id: string;
};

export type GetAgreementCampaignByIDResponse = {
  success: boolean;
  message?: string;
  agreementCampaign?: AgreementCampaign;
};

export type GetAgreementResponsesByAgreementCampaignIDRequest = {
  agreementCampaignId: string;
};

export type GetAgreementResponsesByAgreementCampaignIDResponse = {
  success: boolean;
  message?: string;
  agreementResponses?: AgreementResponse[];
};

export type AgreeToAgreementResponseRequest = {
  email: string;
  password: string;
  companySubdomain: string;
};

export type AgreeToAgreementResponseResponse = {
  success: boolean;
  message?: string;
  companyUser?: CompanyUser;
  accessToken: string;
  refreshToken: string;
};

export type GetFeatureFlagsByCompanyIdRequest = {
  companyId: string;
};

export type GetFeatureFlagsByCompanyIdResponse = {
  success: boolean;
  message?: string;
  companyId: string;
  featureFlags: FeatureFlag[];
};

export type UpdateFeatureFlagStatusByCompanyIdRequest = {
  companyId?: string;
  featureFlagTemplateId;
  status: FeatureFlagStatus;
};

export type UpdateFeatureFlagStatusByCompanyIdResponse = {
  success: boolean;
  message?: string;
};

export type GetReportRequest = {
  surveyCampaignId?: string;
  format: "html" | "pdf";
  team?: string;
  surveyResponseId?: string;
  layout: GetReportRequestLayout;
  comparedTo?: string;
};

export enum GetReportRequestLayout {
  Group = "Group",
  Team = "Team",
  Individual = "Individual",
}